<template>
  <div class="login-container">
    <div class="yarn-ball-decoration"></div>
    <div class="yarn-ball-decoration secondary"></div>
    <el-card class="login-card">
      <div class="card-header">
        <el-button 
          :icon="ArrowLeft" 
          type="default" 
          @click="goBackToHome"
          :plain="false"
          class="back-button"
        ></el-button>
        <h2 class="login-title">🧵 登录到钩织乐园</h2>
      </div>
      <el-form :model="loginForm" :rules="rules" ref="loginFormRef" label-width="0px" class="login-form">
        <el-form-item prop="username">
          <el-input 
            v-model="loginForm.username" 
            :prefix-icon="User"
            placeholder="请输入用户名"
            size="large"
          />
        </el-form-item>
        <el-form-item prop="password">
          <el-input 
            v-model="loginForm.password" 
            :prefix-icon="Lock"
            type="password"
            placeholder="请输入密码"
            size="large"
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <el-form-item>
          <el-button 
            type="primary" 
            size="large" 
            @click="handleLogin" 
            :loading="loading"
            class="login-btn"
          >
            登录
          </el-button>
        </el-form-item>
        <div class="register-hint">
          <p>还没有账号？<a @click="goToRegister" class="link">立即注册</a></p>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { ArrowLeft, User, Lock } from '@element-plus/icons-vue';
import { login } from '@/user/api/auth';
import { useStore } from 'vuex';
import GlobalChatWebSocketService from '@/user/services/globalChatService'; // 导入全局WebSocket服务
import { initWebSocketNotifications } from '@/user/services/notificationService'; // 导入WebSocket通知初始化函数

interface LoginForm {
  username: string;
  password: string;
}

const router = useRouter();
const store = useStore();

const loginForm = reactive<LoginForm>({
  username: '',
  password: ''
});

const loading = ref(false);

const validateUsername = (rule: any, value: any, callback: any) => {
  if (!value) {
    callback(new Error('请输入用户名'));
  } else {
    callback();
  }
};

const validatePassword = (rule: any, value: any, callback: any) => {
  if (!value || value.length < 6) {
    callback(new Error('密码长度至少为6位'));
  } else {
    callback();
  }
};

const rules = reactive({
  username: [{ validator: validateUsername, trigger: 'blur' }],
  password: [{ validator: validatePassword, trigger: 'blur' }]
});

const loginFormRef = ref();

const handleLogin = async () => {
  // 验证表单
  if (!loginFormRef.value) return;
  
  const valid = await loginFormRef.value.validate().catch(() => false);
  if (!valid) return;

  loading.value = true;
  
  try {
    const response = await login(loginForm);
    
    console.log('登录响应:', response); // 添加调试信息
    
    // 检查响应格式
    let loginResult;
    
    // 根据项目规范，后端响应可能有两种格式：
    // 1. 直接是 { code: 1, data: {...}, msg: "..." } 格式
    // 2. 嵌套格式 { data: { code: 1, data: {...}, msg: "..." } }
    
    if (response && typeof response === 'object') {
      if (response.code === 1) {
        // 直接是期望的成功格式
        loginResult = response;
      } else if (response.code !== 1) {
        // 登录失败，直接抛出后端返回的错误信息
        throw new Error(response.msg || '登录失败');
      } else if (response.data && response.data.code === 1) {
        // 嵌套格式
        loginResult = response.data;
      } else if (response.data && response.data.code !== 1) {
        // 嵌套格式的失败响应
        throw new Error(response.data.msg || '登录失败');
      } else {
        // 既不是直接格式也不是嵌套格式
        throw new Error('登录响应格式不正确');
      }
    } else {
      throw new Error('登录响应格式不正确');
    }
    
    // 获取用户数据
    const userData = loginResult.data;
    
    // 验证必需的字段
    if (!userData || !userData.token) {
      throw new Error('登录响应缺少必要数据');
    }
    
    // 将用户信息存储到store
    store.dispatch('setUserInfo', {
      id: userData.id,
      username: userData.username,
      name: userData.name,
      avatar: userData.avatar,
      roleId: userData.roleId
    });
    
    // 存储token到store和localStorage
    store.commit('SET_TOKEN', userData.token);
    localStorage.setItem('token', userData.token);
    
    ElMessage.success('登录成功');
    
    // 登录成功后立即启动WebSocket连接
    console.log('准备启动WebSocket连接...');
    GlobalChatWebSocketService.connect(userData.token);
    
    // 初始化WebSocket通知处理器，确保能接收公告和通知消息
    initWebSocketNotifications();
    
    // 跳转到首页
    await router.push({ name: 'home' });
  } catch (error: any) {
    console.error('登录错误:', error);
    // 显示具体的错误信息
    ElMessage.error(error.message || error.response?.data?.msg || error.message || '登录失败');
  } finally {
    loading.value = false;
  }
};

const goBackToHome = () => {
  router.push({ name: 'home' });
};

const goToRegister = () => {
  router.push({ name: 'register' });
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@500;600&display=swap');

.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../../../background/background.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

.yarn-ball-decoration {
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4a976 0%, #cbb394 100%);
  opacity: 0.2;
  z-index: 0;
}

.yarn-ball-decoration:nth-child(1) {
  top: 10%;
  left: 5%;
  width: 150px;
  height: 150px;
  animation: float 8s infinite ease-in-out;
}

.yarn-ball-decoration.secondary {
  bottom: 15%;
  right: 7%;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #a8977a 0%, #8b6d4b 100%);
  animation: float 10s infinite ease-in-out;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, 20px); }
}

.login-card {
  width: 420px;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(139, 109, 75, 0.2);
  border: 1px solid rgba(212, 169, 118, 0.3);
  background: rgba(255, 250, 240, 0.3);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 10;
  border: 1px solid #e9dcc8;
  /* 添加玻璃流动动画效果 */
  animation: glassFlow 15s ease-in-out infinite alternate;
}

/* 新增玻璃流动动画 */
@keyframes glassFlow {
  0% {
    background: rgba(255, 250, 240, 0.25);
    box-shadow: 0 10px 30px rgba(139, 109, 75, 0.15);
  }
  50% {
    background: rgba(255, 252, 245, 0.35);
    box-shadow: 0 15px 40px rgba(139, 109, 75, 0.25);
  }
  100% {
    background: rgba(254, 248, 235, 0.28);
    box-shadow: 0 12px 35px rgba(139, 109, 75, 0.2);
  }
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  position: relative;
}

.back-button {
  margin-right: 15px;
  border: none;
  background: rgba(212, 169, 118, 0.1);
  color: #8b6d4b;
  transition: all 0.3s;
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.back-button:hover {
  background: rgba(212, 169, 118, 0.2);
  transform: scale(1.05);
}

.login-title {
  text-align: center;
  margin: 0;
  flex: 1;
  color: #5a3e36;
  font-family: 'Quicksand', cursive;
  font-weight: 600;
  font-size: 24px;
}

.login-form {
  width: 100%;
}

.login-form .el-form-item {
  margin-bottom: 20px;
}

.login-form .el-input__wrapper {
  border-radius: 12px;
  border: 1px solid #d4c19c;
  padding: 0 15px;
  background: rgba(255, 255, 255, 0.6);
}

.login-btn {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(to right, #d4a976, #cbb394);
  border: none;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  transition: all 0.3s;
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(212, 169, 118, 0.4);
}

.login-btn:active {
  transform: translateY(0);
}

.register-hint {
  text-align: center;
  margin-top: 20px;
  color: #a8977a;
}

.register-hint p {
  margin: 0;
  font-size: 14px;
}

.link {
  color: #8b6d4b;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
}

.link:hover {
  color: #5a3e36;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-container {
    padding: 20px;
  }
  
  .login-card {
    width: 100%;
    max-width: 400px;
    padding: 25px 20px;
  }
  
  .login-title {
    font-size: 20px;
  }
  
  .yarn-ball-decoration {
    display: none;
  }
}

@media (max-width: 480px) {
  .login-card {
    padding: 20px 15px;
  }
  
  .login-title {
    font-size: 18px;
  }
}
</style>